// Annual Vendor Audit — the registry module at /portfolio-admin/vendor-audits
// (sidebar "Vendors" group).
//
// 🔴 THE PAGE'S JOB IS THE COMPLETENESS CLAIM, not the list. Phoenix has to be
// able to say "we ran a market check on every vendor we owed one to this
// year", so three things are on screen together and none of them may be
// dropped:
//
//   1. the DENOMINATOR — "9 of 14 complete", never a bare count of rows;
//   2. every in-scope vendor, INCLUDING the ones nobody has started;
//   3. the vendors that were EXCLUDED, and why.
//
// (3) is the one that is tempting to hide, and hiding it is what makes the
// claim false: "we audited everyone we could" and "we audited everyone" are
// different sentences, and only the second is the one this module exists to
// support. An exclusion nobody can see is an exclusion nobody will fix.
//
// The table is joined from TWO reads and that is deliberate. `/scope` is the
// only source that knows about a vendor with NO audit — the thing the
// denominator turns on — while the audit list carries the review clock and the
// quote and comment counts. Neither alone can render the row.

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CellDoorControl,
  ExceptionBadge,
  FilterChips,
  PageHeader,
  Skeleton,
  StatusBadge,
  SubTable,
  TableValue,
  type StatusTone,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  useCreateVendorAudit,
  useVendorAuditScope,
  useVendorAudits,
} from "@/hooks/portfolioAdmin/useVendorAudits";
import type {
  VendorAuditExcludedRowDto,
  VendorAuditScopeDto,
  VendorAuditStatus,
  VendorAuditSummaryDto,
} from "@/api/portfolioAdmin/vendorAuditsApi";
import {
  VENDOR_AUDIT_STATUS_LABEL,
  VendorAuditDrawer,
  vendorAuditReviewClock,
  vendorAuditStatusTone,
} from "@/components/portfolioAdmin/vendors/audits/VendorAuditDrawer";

/** Chip vocabulary. `superseded` is deliberately absent: the scope read
 *  resolves the LIVE audit per vendor, so a superseded row can never appear
 *  here, and a chip that can never have rows is a filter that lies. Corrections
 *  are visible on the vendor record's own history strip. */
const STATUS_CHIPS = [
  "all",
  "not-started",
  "draft",
  "in-vendor-review",
  "awaiting-final-assessment",
  "completed",
] as const;

type StatusChipId = (typeof STATUS_CHIPS)[number];

const CHIP_LABEL: Record<StatusChipId, string> = {
  all: "All",
  "not-started": "Not started",
  draft: VENDOR_AUDIT_STATUS_LABEL.draft,
  "in-vendor-review": VENDOR_AUDIT_STATUS_LABEL["in-vendor-review"],
  "awaiting-final-assessment":
    VENDOR_AUDIT_STATUS_LABEL["awaiting-final-assessment"],
  completed: VENDOR_AUDIT_STATUS_LABEL.completed,
};

/** Distinct facts with distinct fixes — invite them, versus re-enable the
 *  account they already have — so they never collapse into one sentence. */
const EXCLUSION_COPY: Record<
  VendorAuditExcludedRowDto["reason"],
  { label: string; door: string }
> = {
  "no portal account": {
    label: "No portal account",
    door: "Invite them to the portal",
  },
  "portal account unusable": {
    label: "Portal account archived or disabled",
    door: "Re-enable their account",
  },
};

/** One in-scope vendor, joined to its live audit's detail. */
interface CoverageRow {
  vendorId: string;
  vendorName: string;
  vendorStatus: string;
  auditId: string | null;
  auditStatus: VendorAuditStatus | null;
  audit: VendorAuditSummaryDto | null;
}

/**
 * The completeness badge, in THREE states.
 *
 * 🔴 Keyed on `inScopeCount`, never on whether the list is empty. Two states
 * cannot tell "nothing is owed" from "everything owed is done" — which is
 * exactly the bug `VendorReportComplianceService` shipped, a loan with no
 * vendors rendering a green "All reports filed".
 */
function coverageBadge(scope: {
  inScopeCount: number;
  completedCount: number;
}): { tone: StatusTone; label: string; hint: string } {
  if (scope.inScopeCount === 0) {
    return {
      tone: "neutral",
      label: "No vendors in scope",
      hint: "No vendor was active or engaged in this year, so none owes an audit.",
    };
  }
  const remaining = scope.inScopeCount - scope.completedCount;
  const hint = `${scope.completedCount} of ${scope.inScopeCount} in-scope vendors have a completed audit for this year.`;
  return remaining > 0
    ? { tone: "warn", label: `${remaining} outstanding`, hint }
    : { tone: "ok", label: "All audits complete", hint };
}

function yearFromParams(params: URLSearchParams): number | undefined {
  const raw = params.get("year");
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 2000 && n <= 2999 ? n : undefined;
}

function statusFromParams(params: URLSearchParams): StatusChipId {
  const raw = params.get("status");
  return STATUS_CHIPS.includes(raw as StatusChipId) ? (raw as StatusChipId) : "all";
}

/**
 * Which chip a row belongs under, or null for a status no chip covers.
 *
 * `superseded` is the only such status, and the scope read cannot produce it
 * — it resolves the LIVE audit per vendor. The null branch is therefore
 * unreachable today and deliberately kept: a row whose status no filter can
 * name must fall out of the counts rather than be forced into the nearest
 * chip, which would make a count disagree with the table under it.
 */
function chipIdFor(status: VendorAuditStatus | null): StatusChipId | null {
  if (status == null) return "not-started";
  return STATUS_CHIPS.includes(status as StatusChipId)
    ? (status as StatusChipId)
    : null;
}

export default function VendorAuditsPage() {
  const toast = useToast();
  const auth = useAuth();
  const { date } = useDateFormatter();
  const [params, setParams] = useSearchParams();

  // Parsed at PARSE time. A post-mount effect that writes the default back
  // into the params re-keys the queries and fires a second request per load.
  const yearParam = yearFromParams(params);
  const statusFilter = statusFromParams(params);
  const openAuditId = params.get("audit");

  const canManage =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");

  // Omitting `year` lets the SERVER resolve the current audit year — it uses
  // the Phoenix calendar year, and a client-derived one disagrees with it for
  // seven hours every 1 January.
  const scopeQuery = useVendorAuditScope(yearParam);
  const effectiveYear = yearParam ?? scopeQuery.data?.auditYear;

  const auditsQuery = useVendorAudits(
    { year: effectiveYear },
    { enabled: effectiveYear != null },
  );

  const createAudit = useCreateVendorAudit();
  const [startingVendorId, setStartingVendorId] = useState<string | null>(null);

  const setParam = (key: string, value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set(key, value);
    else updated.delete(key);
    setParams(updated, { replace: true });
  };

  const scope: VendorAuditScopeDto | undefined = scopeQuery.data;

  const rows: CoverageRow[] = useMemo(() => {
    if (!scope) return [];
    const byId = new Map((auditsQuery.data ?? []).map((a) => [a.id, a]));
    return scope.inScope.map((r) => ({
      vendorId: r.vendorId,
      vendorName: r.vendorName,
      vendorStatus: r.vendorStatus,
      auditId: r.auditId,
      auditStatus: r.auditStatus,
      audit: r.auditId ? (byId.get(r.auditId) ?? null) : null,
    }));
  }, [scope, auditsQuery.data]);

  const chipCounts = useMemo(() => {
    const counts: Record<StatusChipId, number> = {
      all: rows.length,
      "not-started": 0,
      draft: 0,
      "in-vendor-review": 0,
      "awaiting-final-assessment": 0,
      completed: 0,
    };
    for (const row of rows) {
      const key = chipIdFor(row.auditStatus);
      if (key) counts[key] += 1;
    }
    return counts;
  }, [rows]);

  const visibleRows = useMemo(
    () =>
      statusFilter === "all"
        ? rows
        : rows.filter((r) => chipIdFor(r.auditStatus) === statusFilter),
    [rows, statusFilter],
  );

  const onStartAudit = async (row: CoverageRow) => {
    if (effectiveYear == null) return;
    setStartingVendorId(row.vendorId);
    try {
      const created = await createAudit.mutateAsync({
        vendorId: row.vendorId,
        auditYear: effectiveYear,
      });
      toast.success(`${effectiveYear} audit opened for ${row.vendorName}.`);
      setParam("audit", created.id);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setStartingVendorId(null);
    }
  };

  const columns: SubTableColumn<CoverageRow>[] = [
    {
      key: "vendor",
      header: "Vendor",
      sortAccessor: (row) => row.vendorName,
      render: (row) => (
        <span className="inline-flex items-center">
          <Link
            to={`/portfolio-admin/vendors/${row.vendorId}?tab=annual-audit`}
            className="text-gold-shadow hover:underline"
            data-testid={`vendor-audit-vendor-link-${row.vendorId}`}
          >
            {row.vendorName}
          </Link>
          {/* An exception is a fact about this row's subject, so it sits
              inline after the name rather than owning a column that is empty
              on every healthy row. */}
          {row.audit &&
            (() => {
              const clock = vendorAuditReviewClock(row.audit);
              return clock?.severity ? (
                <ExceptionBadge severity={clock.severity}>
                  {clock.label}
                </ExceptionBadge>
              ) : null;
            })()}
        </span>
      ),
    },
    {
      key: "vendorStatus",
      header: "Vendor status",
      sortAccessor: (row) => row.vendorStatus,
      render: (row) => (
        <span className="text-gray-mid capitalize">{row.vendorStatus}</span>
      ),
    },
    {
      key: "auditStatus",
      header: "Audit",
      sortAccessor: (row) => row.auditStatus ?? "",
      render: (row) => {
        if (row.auditStatus && row.auditId) {
          return (
            <button
              type="button"
              onClick={() => setParam("audit", row.auditId!)}
              aria-label={`Open the audit for ${row.vendorName}`}
              className="rounded-pill focus-visible:outline-none focus-visible:shadow-goldRing"
              data-testid={`vendor-audit-open-${row.auditId}`}
            >
              <StatusBadge tone={vendorAuditStatusTone(row.auditStatus)}>
                {VENDOR_AUDIT_STATUS_LABEL[row.auditStatus]}
              </StatusBadge>
            </button>
          );
        }
        // An actionable absence: the muted state label AND its door. Rendering
        // it as an em dash would hide work from the person whose job it is.
        return (
          <span className="inline-flex flex-col items-start gap-space-1">
            <span className="text-caption text-gray-mid">Not started</span>
            {canManage && (
              <CellDoorControl
                door={{
                  kind: "stamp",
                  label: "Start audit",
                  onClick: () => void onStartAudit(row),
                  disabled: startingVendorId != null,
                  disabledReason:
                    startingVendorId === row.vendorId ? "Opening…" : undefined,
                }}
              />
            )}
          </span>
        );
      },
    },
    {
      key: "quotes",
      header: "Competitors",
      align: "right",
      sortAccessor: (row) => row.audit?.quoteCount ?? -1,
      render: (row) => <TableValue value={row.audit?.quoteCount ?? null} />,
    },
    {
      key: "comments",
      header: "Comments",
      align: "right",
      sortAccessor: (row) => row.audit?.commentCount ?? -1,
      render: (row) => <TableValue value={row.audit?.commentCount ?? null} />,
    },
    {
      key: "opened",
      header: "Opened",
      sortAccessor: (row) => row.audit?.createdAt ?? "",
      render: (row) => (
        <TableValue value={row.audit ? date(row.audit.createdAt) : null} />
      ),
    },
  ];

  const excludedColumns: SubTableColumn<VendorAuditExcludedRowDto>[] = [
    {
      key: "vendor",
      header: "Vendor",
      sortAccessor: (row) => row.vendorName,
      render: (row) => (
        <Link
          to={`/portfolio-admin/vendors/${row.vendorId}?tab=portal-account`}
          className="text-gold-shadow hover:underline"
          data-testid={`vendor-audit-excluded-link-${row.vendorId}`}
        >
          {row.vendorName}
        </Link>
      ),
    },
    {
      key: "vendorStatus",
      header: "Vendor status",
      sortAccessor: (row) => row.vendorStatus,
      render: (row) => (
        <span className="text-gray-mid capitalize">{row.vendorStatus}</span>
      ),
    },
    {
      key: "reason",
      header: "Why",
      sortAccessor: (row) => row.reason,
      render: (row) => (
        <span
          className="inline-flex flex-col items-start gap-space-1"
          data-testid={`vendor-audit-excluded-reason-${row.vendorId}`}
        >
          <span className="text-caption text-gray-mid">
            {EXCLUSION_COPY[row.reason].label}
          </span>
          <Link
            to={`/portfolio-admin/vendors/${row.vendorId}?tab=portal-account`}
            className="text-caption text-gold-shadow hover:underline"
          >
            {EXCLUSION_COPY[row.reason].door}
          </Link>
        </span>
      ),
    },
  ];

  const badge = scope ? coverageBadge(scope) : null;
  const yearLabel = effectiveYear != null ? String(effectiveYear) : "";

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-audits-page">
      <PageHeader
        eyebrow="Portfolio Administration"
        title="Annual Audit"
        subtitle="The yearly market check on every vendor — what Phoenix pays, what competitors quoted, and the determination that followed."
      />

      {/* Year scopes the whole page, so it sits above both cards rather than
          inside one of them. */}
      <div className="flex flex-wrap items-center gap-space-2">
        <label className="flex items-center gap-space-2 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Audit year</span>
          {effectiveYear == null ? (
            <Skeleton className="h-9 w-24" />
          ) : (
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={effectiveYear}
              onChange={(e) => setParam("year", e.target.value)}
              data-testid="vendor-audits-year-filter"
            >
              {/* The CLIENT's year builds the option LIST only — the selected
                  default always comes from the server. */}
              {yearOptions(effectiveYear).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}
        </label>
      </div>

      <Card>
        <div className="flex items-start justify-between gap-space-3 flex-wrap mb-space-3">
          <div>
            <h2 className="font-display font-bold text-h3 m-0">
              In-scope vendors{yearLabel ? ` — ${yearLabel}` : ""}
            </h2>
            {scope && (
              <p
                className="text-small text-gray-mid m-0 mt-space-1"
                data-testid="vendor-audits-completeness"
              >
                <span className="tabular-nums font-medium text-onyx">
                  {scope.completedCount} of {scope.inScopeCount}
                </span>{" "}
                complete
              </p>
            )}
          </div>
          {badge && (
            <StatusBadge
              tone={badge.tone}
              hint={badge.hint}
              data-testid="vendor-audits-coverage-badge"
            >
              {badge.label}
            </StatusBadge>
          )}
        </div>

        <FilterChips
          className="mb-space-3"
          label="Audit status"
          activeId={statusFilter}
          onChange={(id) => setParam("status", id === "all" ? null : id)}
          chips={STATUS_CHIPS.map((id) => ({
            id,
            label: CHIP_LABEL[id],
            count: chipCounts[id],
          }))}
        />

        {scopeQuery.isLoading && (
          <div className="flex flex-col gap-space-2" data-testid="vendor-audits-loading">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {scopeQuery.isError && (
          <div data-testid="vendor-audits-error">
            <p className="text-small text-status-error m-0" role="alert">
              Could not load the audit scope for this year.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-space-2"
              onClick={() => void scopeQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        )}

        {scope && (
          <SubTable
            caption="In-scope vendors"
            columns={columns}
            rows={visibleRows}
            rowKey={(row) => row.vendorId}
            rowTestId={(row) => `vendor-audit-row-${row.vendorId}`}
            data-testid="vendor-audits-table"
            empty={{
              message:
                statusFilter === "all"
                  ? `No vendor owes an audit for ${yearLabel}.`
                  : `No in-scope vendor is at “${CHIP_LABEL[statusFilter]}” for ${yearLabel}.`,
            }}
          />
        )}
      </Card>

      {/* 🔴 Never netted out of the denominator, and never hidden when empty
          would be convenient. The compliance story depends on this group being
          visible and each row carrying the fix for its own reason. */}
      <Card>
        <div className="flex items-start justify-between gap-space-3 flex-wrap mb-space-3">
          <div>
            <h2 className="font-display font-bold text-h3 m-0">
              Excluded — no usable portal account
            </h2>
            <p className="text-small text-gray-mid m-0 mt-space-1">
              These vendors would owe an audit, but the release step needs a
              portal account for them to comment through. They are reported
              here rather than removed from the count.
            </p>
          </div>
          {scope && (
            <StatusBadge
              tone={scope.excludedNoPortalAccount.length > 0 ? "warn" : "neutral"}
              data-testid="vendor-audits-excluded-badge"
            >
              {scope.excludedNoPortalAccount.length} excluded
            </StatusBadge>
          )}
        </div>

        {scope && (
          <SubTable
            caption="Vendors excluded from this year's audit"
            columns={excludedColumns}
            rows={scope.excludedNoPortalAccount}
            rowKey={(row) => row.vendorId}
            rowTestId={(row) => `vendor-audit-excluded-${row.vendorId}`}
            data-testid="vendor-audits-excluded-table"
            empty={{
              message: `Every vendor in scope for ${yearLabel} has a usable portal account.`,
            }}
          />
        )}
      </Card>

      {openAuditId && (
        <VendorAuditDrawer
          auditId={openAuditId}
          open={!!openAuditId}
          onClose={() => setParam("audit", null)}
          onReplaced={(replacementId) => setParam("audit", replacementId)}
        />
      )}
    </div>
  );
}

/** The last six years, plus whatever year is actually selected so the control
 *  can always represent its own value. */
function yearOptions(selected: number): number[] {
  const clientYear = new Date().getFullYear();
  const years = new Set<number>();
  for (let y = clientYear; y >= clientYear - 5; y -= 1) years.add(y);
  years.add(selected);
  return [...years].sort((a, b) => b - a);
}
